/**
 * GLOBAL CONSTANTS
 *
 * Centralized configuration for all magic numbers, rates, and constants used throughout the application.
 * Update these values here instead of hunting through the codebase.
 */

// ============================================================================
// TAX RATES (French BNC & Flat Tax)
// ============================================================================

/**
 * Default BNC (Bénéfices Non Commerciaux) rate for mining income
 * Includes social charges (~25%) + income tax (~9%)
 * Effective from 2024-2025 tax year
 */
export const DEFAULT_BNC_RATE = 0.34;

/**
 * French flat tax rate on capital gains (plus-values)
 * Effective since January 1, 2026
 * Previously 30%, updated to 31.4%
 */
export const FLAT_TAX_RATE = 0.314;

/**
 * Maximum BNC rate (for validation)
 */
export const MAX_BNC_RATE = 0.60;

/**
 * Minimum BNC rate (for validation)
 */
export const MIN_BNC_RATE = 0.01;

// ============================================================================
// PROFIT MARGINS & PRICE CALCULATIONS
// ============================================================================

/**
 * Minimum profit margin applied to minimum sale price calculations
 * Used in: calculateMinimumSalePrice, calculateMinimumSalePriceForMining
 * 5% margin = 1.05 multiplier
 */
export const MINIMUM_PROFIT_MARGIN = 1.05;

/**
 * Minimum profit margin as percentage (5%)
 */
export const MINIMUM_PROFIT_MARGIN_PERCENT = 5;

// ============================================================================
// ELECTRICITY COST CALCULATIONS
// ============================================================================

/**
 * Hours per day (used in consumption calculations)
 */
export const HOURS_PER_DAY = 24;

/**
 * Hours per month (30 days average)
 */
export const HOURS_PER_MONTH = 720;

/**
 * Days per month (average)
 */
export const DAYS_PER_MONTH = 30;

/**
 * Watt-hours to kilowatt-hours conversion factor
 * 1 kWh = 1000 Wh
 */
export const WH_TO_KWH_CONVERSION = 1000;

/**
 * Default HC/HP split ratio for electricity consumption
 * Assumes 50/50 split between off-peak (HC) and peak (HP) hours
 * when detailed information is not available
 */
export const DEFAULT_HC_HP_RATIO = 0.5;

/**
 * Default solar panel production ratio
 * When mode is direct kWh, solar production cannot be automatically calculated
 */
export const SOLAR_PRODUCTION_RATIO_DIRECT_MODE = 0;

// ============================================================================
// PRECISION & ROUNDING
// ============================================================================

/**
 * Decimal places for rounding cryptocurrency amounts
 * Cryptocurrencies can have up to 8 decimal places (satoshi precision for Bitcoin)
 */
export const CRYPTO_DECIMAL_PLACES = 8;

/**
 * Multiplier for precise rounding (10^8)
 * Used in: roundAmount()
 */
export const CRYPTO_ROUNDING_MULTIPLIER = 100000000;

/**
 * Decimal places for currency amounts (EUR)
 * Standard currency precision
 */
export const CURRENCY_DECIMAL_PLACES = 2;

/**
 * Decimal places for percentages in calculations
 */
export const PERCENTAGE_DECIMAL_PLACES = 2;

// ============================================================================
// PAGINATION & DISPLAY
// ============================================================================

/**
 * Default number of items per page in lists
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Maximum items to display in a dropdown
 */
export const MAX_DROPDOWN_ITEMS = 50;

/**
 * Default chart history months to display
 */
export const DEFAULT_CHART_MONTHS = 12;

/**
 * Maximum chart history months
 */
export const MAX_CHART_MONTHS = 60;

// ============================================================================
// DATES & TIME
// ============================================================================

/**
 * Standard date format string (ISO 8601)
 */
export const DATE_FORMAT_ISO = 'YYYY-MM-DD';

/**
 * French date format for display
 */
export const DATE_FORMAT_FR = 'DD/MM/YYYY';

/**
 * Date and time format for display
 */
export const DATE_TIME_FORMAT_FR = 'DD/MM/YYYY HH:mm';

/**
 * Timezone offset for France (UTC+1 standard, UTC+2 daylight)
 */
export const FRANCE_TIMEZONE = 'Europe/Paris';

/**
 * Locale for French formatting
 */
export const LOCALE_FR = 'fr-FR';

// ============================================================================
// CACHE & API LIMITS
// ============================================================================

/**
 * In-memory price cache TTL (Time To Live) in milliseconds
 * CoinGecko API is heavily rate-limited, so we cache for 5 minutes
 */
export const PRICE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Delay between sequential API requests in milliseconds
 * Respects free tier rate limits (~10-50 calls/minute)
 */
export const API_REQUEST_DELAY_MS = 200;

/**
 * Maximum retries for failed API requests
 */
export const MAX_API_RETRIES = 3;

/**
 * Timeout for API requests in milliseconds
 */
export const API_REQUEST_TIMEOUT_MS = 10000;

/**
 * Maximum number of coin ids per CoinGecko /coins/markets batched request
 * Kept well under URL length limits; a typical portfolio fits in a single chunk
 */
export const COINS_MARKETS_CHUNK_SIZE = 250;

// ============================================================================
// VALIDATION LIMITS
// ============================================================================

/**
 * Maximum transaction amount (sanity check)
 * 1 billion EUR
 */
export const MAX_TRANSACTION_AMOUNT = 1_000_000_000;

/**
 * Minimum transaction amount
 * 0.00000001 (smallest crypto unit)
 */
export const MIN_TRANSACTION_AMOUNT = 0.00000001;

/**
 * Maximum electricity rate (per kWh)
 * Sanity check - no rate should exceed 1 EUR/kWh
 */
export const MAX_ELECTRICITY_RATE = 1;

/**
 * Minimum electricity rate (per kWh)
 * Even free electricity is 0, so this is just a sanity check boundary
 */
export const MIN_ELECTRICITY_RATE = 0;

/**
 * Maximum power consumption (watts)
 * Sanity check - no realistic mining setup exceeds 100 kW
 */
export const MAX_POWER_CONSUMPTION = 100000;

/**
 * Minimum days for mining duration
 * At least 1 day of mining for valid entry
 */
export const MIN_MINING_DAYS = 1;

/**
 * Maximum days for mining duration (10 years)
 */
export const MAX_MINING_DAYS = 3650;

// ============================================================================
// UI CONFIGURATION
// ============================================================================

/**
 * Number of significant decimal places for display
 * Show more decimals for small values, fewer for large values
 */
export const SMALL_VALUE_THRESHOLD = 0.01; // < 0.01€ shows 8 decimals
export const MEDIUM_VALUE_THRESHOLD = 1; // < 1€ shows 6 decimals
export const LARGE_VALUE_THRESHOLD = 100; // < 100€ shows 4 decimals

/**
 * Default currency for display
 */
export const DEFAULT_CURRENCY = 'EUR';

/**
 * Default language
 */
export const DEFAULT_LANGUAGE = 'fr';

/**
 * Toast notification duration (milliseconds)
 */
export const TOAST_DURATION_MS = 3000;

/**
 * Loading spinner timeout (milliseconds)
 */
export const LOADING_TIMEOUT_MS = 5000;

// ============================================================================
// CRYPTO TRADING PAIRS
// ============================================================================

/**
 * Main fiat currency for pricing
 */
export const FIAT_CURRENCY = 'EUR';

/**
 * Alternative fiat currencies supported
 */
export const SUPPORTED_FIAT = ['EUR', 'USD', 'GBP'] as const;

/**
 * Major cryptocurrencies (for favorites, filtering)
 */
export const MAJOR_CRYPTOS = ['bitcoin', 'ethereum', 'litecoin', 'ripple', 'cardano'];

// ============================================================================
// TRANSACTION TYPES
// ============================================================================

/**
 * Mining income (payout received from mining pool)
 */
export const TRANSACTION_TYPE_PAYOUT = 'payout';

/**
 * Token purchase
 */
export const TRANSACTION_TYPE_ACHAT = 'achat';

/**
 * Staking reward
 */
export const TRANSACTION_TYPE_STACKING = 'stacking';

/**
 * Token sale
 */
export const TRANSACTION_TYPE_VENTE = 'vente';

/**
 * Cryptocurrency exchange
 */
export const TRANSACTION_TYPE_SWAP = 'swap';

/**
 * Transfer to exchange (neutral, not a taxable event)
 */
export const TRANSACTION_TYPE_TRANSFERT = 'transfert';

/**
 * All transaction types
 */
export const ALL_TRANSACTION_TYPES = [
  TRANSACTION_TYPE_PAYOUT,
  TRANSACTION_TYPE_ACHAT,
  TRANSACTION_TYPE_STACKING,
  TRANSACTION_TYPE_VENTE,
  TRANSACTION_TYPE_SWAP,
  TRANSACTION_TYPE_TRANSFERT,
] as const;

// ============================================================================
// DECLARATION FREQUENCIES
// ============================================================================

/**
 * Monthly declaration frequency
 */
export const FREQUENCY_MONTHLY = 'mensuel';

/**
 * Quarterly declaration frequency
 */
export const FREQUENCY_QUARTERLY = 'trimestriel';

/**
 * Annual declaration frequency
 */
export const FREQUENCY_ANNUAL = 'annuel';

/**
 * All declaration frequencies
 */
export const ALL_FREQUENCIES = [
  FREQUENCY_MONTHLY,
  FREQUENCY_QUARTERLY,
  FREQUENCY_ANNUAL,
] as const;

// ============================================================================
// ELECTRICITY TARIFF TYPES
// ============================================================================

/**
 * Simple (uniform) electricity tariff
 */
export const TARIFF_SIMPLE = 'simple';

/**
 * HC/HP (off-peak/peak) electricity tariff
 */
export const TARIFF_HC_HP = 'hc_hp';

/**
 * All tariff types
 */
export const ALL_TARIFF_TYPES = [TARIFF_SIMPLE, TARIFF_HC_HP] as const;

// ============================================================================
// FILE & STORAGE
// ============================================================================

/**
 * Encrypted backup file extension
 */
export const BACKUP_FILE_EXTENSION = '.enc';

/**
 * Maximum backup file size (100 MB)
 */
export const MAX_BACKUP_FILE_SIZE = 100 * 1024 * 1024;

/**
 * localStorage key for encrypted payouts
 */
export const STORAGE_KEY_PAYOUTS = 'cryptotrack_encrypted_payouts';

/**
 * localStorage key for encrypted settings
 */
export const STORAGE_KEY_SETTINGS = 'cryptotrack_encrypted_settings';

/**
 * localStorage key for mining configurations
 */
export const STORAGE_KEY_MINING_CONFIGS = 'cryptotrack_mining_configs';

// ============================================================================
// FEATURE FLAGS & EXPERIMENTAL
// ============================================================================

/**
 * Enable Sentry error tracking (requires VITE_SENTRY_DSN env var)
 */
export const ENABLE_SENTRY = !!import.meta.env.VITE_SENTRY_DSN;

/**
 * Enable Waltio export feature
 */
export const ENABLE_WALTIO_EXPORT = true;

/**
 * Enable flat tax simulator
 */
export const ENABLE_FLAT_TAX_SIMULATOR = true;

/**
 * Enable capital gains calculator
 */
export const ENABLE_CAPITAL_GAINS = true;

/**
 * Enable mining cost calculator
 */
export const ENABLE_MINING_COSTS = true;
